// @flow

/* eslint-disable react/no-danger */
/* eslint-disable css-modules/no-unused-class */

/**
 * This component requires either the "enable_cpm_content (_mobile)" placement
 * or use of the CpmParamsEndpoint
 *
 * Sample config for use in a placement: renderer_params: component:cpm.ad-target, size:728x90-970x66, position:a
 *
 * All Ad target Ids must be included in the servlet map in AdDecider.java
 *
 * Note: If you're working in a modal context (maps, photoviewer) see ad-target-for-modal.jsx
 *
 */

import React from 'react';
import classNames, { removeAndAdd } from '@ta/common.classnames';
import WindowResize from '@ta/events.window-resize';
import { injectAd, useAdStatus } from '@ta/cpm.utils';
import { trackAdPerformance } from '@ta/cpm.adsupport';
import styles from './ad-target.less';

const DESKTOP_PRF_MEASURE_FIRST_LOAD = 'dt_time_to_first_ad';
const DESKTOP_HEADER_ID = 'gpt-ad-728x90-970x66-header';

// Ensure a valid size
export type ValidSize =
  | '1x1'
  | '160x600'
  | '300x250'
  | '300x250-8x8'
  | '300x600'
  | '300x250-300x600'
  | '728x90'
  | '728x90-970x66'
  | '970x250-728x90'
  | '320x50'
  | '3x1'
  | '4x4'
  | '8x8'
  | 'fluid';

// Ensure proper styling based on size
export const wrapperClasses = {
  '1x1': 'ad iab_',
  '160x600': 'iab_supSky',
  '300x250': 'iab_medRec',
  '300x250-8x8': 'iab_medRec',
  '300x600': 'iab_medRec',
  '728x90': 'iab_leaBoa',
  '970x250': 'iab_bilBrd',
  '320x50': 'inline_ad_wrapper',
  fluid: 'iab_fluid',
};

export function buildSizeAttribute(size: string) {
  const strSizes = size.toLowerCase().split('-');
  const numSizes = [];
  strSizes.forEach((str, idx, arr) => {
    // "300x250" -> [300,250]
    numSizes.push(arr[idx].split('x').map(Number));
  });
  return numSizes.length === 1 ? JSON.stringify(numSizes[0]) : JSON.stringify(numSizes);
}

export type Props = {|
  size: ValidSize, // 300x250 etc.
  position?: string, // a, b, c, -> 300x250-a
  additionalClass?: string, // Deprecated (style theb wrapper instead)
  additionalWrapperClass?: string, // for any (minimal!) additional styling on the ad parent element
  pageLoadRender?: boolean | string, // if false, class="gptAd delayAd", all ads must be injected manually
  inactive?: boolean, // class="gpt inactive", an ad will not be injected, applied when ad target will be hidden due to browser resize etc.
  centered?: boolean, // ad-target will be centered to its parent, ignored by mobile, since mobile always centers
  withMargin?: boolean, // ad-target will have 12px top/bottom margin
  withBottomMargin?: boolean, // ad-target will have 24px bottom margin
  minWinSize?: string, // Optional screen size minimum below which the ad will be hidden/disabled
  reserveHeight?: boolean | string,
|};

function getBool(propValue) {
  return String(propValue).toLowerCase() === 'true';
}

function AdTarget(props: Props) {
  const {
    size,
    position = '',
    additionalClass = '',
    additionalWrapperClass = '',
    pageLoadRender = true,
    inactive = false,
    centered = false,
    withMargin = false,
    withBottomMargin = false,
    minWinSize = '0',
    reserveHeight = false,
  } = props;

  const wrapperRef = React.useRef(null);

  function buildTargetClass() {
    let targetClasses = classNames('gptAd', styles.gptAd);
    if (getBool(inactive)) {
      // Skip pageload injection, disable refreshes
      targetClasses = removeAndAdd(targetClasses, null, 'inactive').join(' ');
    }
    if (!getBool(pageLoadRender)) {
      // Skip pageload injection, manually inject
      targetClasses = removeAndAdd(targetClasses, null, 'delayAd').join(' ');
    }
    if (centered) {
      targetClasses = removeAndAdd(targetClasses, null, styles.centered).join(' ');
    }
    if (withMargin) {
      targetClasses = removeAndAdd(targetClasses, null, styles.withMargin).join(' ');
    }
    if (withBottomMargin) {
      targetClasses = removeAndAdd(targetClasses, null, styles.withBottomMargin).join(' ');
    }
    if (additionalClass) {
      targetClasses = removeAndAdd(targetClasses, null, additionalClass).join(' ');
    }
    return targetClasses;
  }

  function buildWrapperClass() {
    const firstSize = size.split('-')[0].toLowerCase();
    const resultClasses = [wrapperClasses[firstSize] || ''];
    if (
      // once we remove all a/b/top/bottom positions we can just check if position contains 'rail'
      !position.includes('inline') &&
      !position.includes('footer') &&
      (resultClasses.includes('iab_medRec') || resultClasses.includes('iab_supSky'))
    ) {
      resultClasses.push(styles.sidebarAd);
    }
    if (additionalWrapperClass) {
      if (additionalWrapperClass === 'whiteBg') {
        resultClasses.push(styles.whiteBg);
      } else if (additionalWrapperClass === 'whiteBgWithPadding') {
        resultClasses.push(styles.whiteBgWithPadding);
      } else if (additionalWrapperClass === 'grayBg') {
        resultClasses.push(styles.grayBg);
      } else if (additionalWrapperClass === 'grayBgWithPadding') {
        resultClasses.push(styles.grayBgWithPadding);
      } else if (additionalWrapperClass === 'outerLeft') {
        resultClasses.push(styles.outerLeft);
      } else if (additionalWrapperClass === 'outerRight') {
        resultClasses.push(styles.outerRight);
      } else {
        resultClasses.push(additionalWrapperClass);
      }
    }
    if (reserveHeight) {
      resultClasses.push(styles.verticallyCentered);
    }
    return resultClasses.join(' ');
  }

  function disableTargetAtMinWinSize() {
    if (minWinSize !== '0') {
      const minWidth = parseInt(minWinSize, 10);
      // eslint-disable-next-line no-restricted-globals
      const screenWidth = window.innerWidth;
      let target;
      if (wrapperRef.current) {
        target = wrapperRef.current.querySelector('.gptAd');
      }
      if (target && target.classList) {
        if (screenWidth < minWidth) {
          target.classList.add('inactive');
          target.classList.add('hidden');
        } else if (target.classList.contains('inactive')) {
          target.classList.remove('inactive');
          target.classList.remove('hidden');
        }
      }
    }
  }

  function getMinHeight() {
    const possibleHeights = size.split('-').map(s => {
      const height = s.split('x')[1];
      return parseInt(height, 10);
    });
    return Math.max(...possibleHeights);
  }

  const wrapperClass = buildWrapperClass();
  const targetClass = buildTargetClass();
  const targetSizeAttribute = buildSizeAttribute(size);
  const targetSize = size.toLowerCase();
  const targetPosition = position ? `-${position}`.toLowerCase() : '';
  const adId = `gpt-ad-${targetSize}${targetPosition}`;
  const minHeight = getBool(reserveHeight) ? `${getMinHeight()}px` : undefined;

  const { isRendered } = useAdStatus(adId);

  React.useEffect(() => {
    if (!getBool(pageLoadRender)) injectAd(adId);
  }, [pageLoadRender, adId]);

  React.useEffect(() => disableTargetAtMinWinSize());

  React.useEffect(() => {
    if (adId === DESKTOP_HEADER_ID) {
      trackAdPerformance(DESKTOP_PRF_MEASURE_FIRST_LOAD, isRendered, true);
    }
  }, [adId, isRendered]);

  // The ad target class/id is manipulated by Ads JS after injection
  const targetHTML = `<div id="${adId}" class="${targetClass}" data-size="${targetSizeAttribute}"></div>`;

  return (
    <>
      {minWinSize !== '0' && <WindowResize callback={disableTargetAtMinWinSize} throttle={300} />}
      <div
        className={wrapperClass}
        style={{ minHeight }}
        ref={wrapperRef}
        dangerouslySetInnerHTML={{ __html: targetHTML }}
      />
    </>
  );
}

export default AdTarget;
